import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { MapPin, Ruler, Wallet, Layers, Sparkles, ArrowRight, CheckCircle, AlertTriangle, XCircle, Loader2, Download, MessageSquare } from 'lucide-react';
import api, { LOCATIONS, PROJECT_TYPES, FINISH_LEVELS, BUILDING_TYPES, formatZARFull, trackEvent } from '@/lib/api';
import ReportLeadGate from '@/components/ReportLeadGate';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const FEASIBILITY_COLORS = {
  excellent: { bg: 'bg-green-500/10', text: 'text-green-600', border: 'border-green-500/30', icon: CheckCircle },
  good: { bg: 'bg-lime-500/10', text: 'text-lime-600', border: 'border-lime-500/30', icon: CheckCircle },
  tight: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', border: 'border-yellow-500/30', icon: AlertTriangle },
  challenging: { bg: 'bg-orange-500/10', text: 'text-orange-600', border: 'border-orange-500/30', icon: AlertTriangle },
  unlikely: { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/30', icon: XCircle },
};

export default function FeasibilityTool() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    project_type: searchParams.get('project_type') || '',
    location: searchParams.get('location') || '',
    building_type: '',
    stand_size: searchParams.get('stand_size') || '',
    budget: searchParams.get('budget') || '',
    finish_level: 'standard',
    email: '', name: '', phone: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiExplanation, setAiExplanation] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [gateSubmitted, setGateSubmitted] = useState(false);

  useEffect(() => { trackEvent('page_view', { page: 'feasibility_tool' }); }, []);

  // Auto-submit if all params from hero
  useEffect(() => {
    if (searchParams.get('project_type') && searchParams.get('location') && searchParams.get('stand_size') && searchParams.get('budget')) {
      handleCalculate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCalculate = async () => {
    if (!form.project_type || !form.location || !form.stand_size || !form.budget) {
      toast.error('Please fill in all required fields');
      return;
    }
    // Fire impression at the top of the funnel — visitor completed criteria
    // form and clicked Calculate. Non-blocking.
    api.post('/tools/impression', {
      tool: 'feasibility',
      criteria: {
        project_type: form.project_type, location: form.location,
        stand_size: Number(form.stand_size), budget: Number(form.budget),
        finish_level: form.finish_level, building_type: form.building_type,
      },
    }).catch(() => {});
    // If we already captured lead details in this session, run the calc
    // straight away. Otherwise gate on Name/Email/Phone first.
    if (gateSubmitted) {
      runCalculation();
      return;
    }
    setShowGate(true);
  };

  const runCalculation = async (contact) => {
    setLoading(true);
    setResult(null);
    setAiExplanation('');
    try {
      const payload = {
        ...form,
        ...(contact || {}),
        stand_size: Number(form.stand_size),
        budget: Number(form.budget),
      };
      // Persist any contact details captured by the gate on the parent form
      // so downstream actions (PDF download, save, etc.) don't re-ask.
      if (contact) setForm((f) => ({ ...f, ...contact }));
      const { data } = await api.post('/feasibility/calculate', payload);
      setResult(data);
      setGateSubmitted(true);
      setShowGate(false);
      trackEvent('feasibility_calculated', { feasibility: data.feasibility, location: form.location });
      toast.success('Feasibility report generated');
      fetchAiExplanation(data);
    } catch (e) {
      toast.error('Calculation failed. Please try again.');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const fetchAiExplanation = async (data) => {
    setAiLoading(true);
    try {
      const { data: aiData } = await api.post('/ai/explain', {
        type: 'feasibility',
        data: {
          location: LOCATIONS.find(l => l.value === form.location)?.label || form.location,
          stand_size: form.stand_size, budget: Number(form.budget),
          project_type: form.project_type, buildable_area: data.buildable_area,
          cost_min: data.cost_range.min, cost_max: data.cost_range.max,
          feasibility: data.feasibility, complexity: data.complexity,
        }
      });
      setAiExplanation(aiData.explanation);
    } catch { setAiExplanation(''); }
    finally { setAiLoading(false); }
  };

  const handleSaveContact = async () => {
    if (!form.email) { toast.error('Please enter your email'); return; }
    try {
      await api.post('/feasibility/calculate', {
        ...form, stand_size: Number(form.stand_size), budget: Number(form.budget),
      });
      toast.success('Your report has been saved! We\'ll be in touch.');
      setShowContact(false);
      trackEvent('feasibility_contact_saved', { email: form.email });
    } catch { toast.error('Failed to save. Please try again.'); }
  };

  const handleDownloadPDF = async () => {
    if (!result) return;
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND}/api/reports/feasibility/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...result, ai_explanation: aiExplanation, email: form.email, name: form.name }),
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = blobUrl;
      link.setAttribute('download', 'raum-feasibility-report.pdf');
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 200);
      trackEvent('feasibility_pdf_download');
      // Fire funnel event so the studio dashboard counts this download.
      api.post('/tools/pdf-download', { tool: 'feasibility', report_id: result?.report_id || '', email: form.email || '' }).catch(() => {});
      toast.success('Report downloaded');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleGateSubmit = async ({ name, email, phone }) => {
    // Called by <ReportLeadGate>. Persist details onto the parent form, run
    // the actual calc (which creates the lead server-side), and let the modal
    // close via the setShowGate(false) inside runCalculation on success.
    setForm((f) => ({ ...f, name, email, phone }));
    await runCalculation({ name, email, phone });
    trackEvent('feasibility_lead_captured', { email });
  };

  const fStyle = result ? FEASIBILITY_COLORS[result.feasibility] || FEASIBILITY_COLORS.tight : null;
  const FIcon = fStyle?.icon || CheckCircle;

  return (
    <div className="min-h-screen bg-brand-light pt-20" data-testid="feasibility-page">
      {/* Header */}
      <div className="section-dark py-12 sm:py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-brand-accent font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Interactive Tool</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-white mb-4" style={{lineHeight: 1.1}}>Is This Possible?</h1>
          <p className="text-white/70 font-body text-base sm:text-base lg:text-lg max-w-2xl leading-relaxed">
            Understand whether your idea can realistically work on your site and budget. Based on comparable projects and local conditions in South Africa.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 sm:py-12">
        {/* Input Form */}
        <div className="bg-white rounded-sm border border-black/10 p-6 sm:p-8 mb-8" data-testid="feasibility-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5 flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> Project Type *
              </Label>
              <Select value={form.project_type} onValueChange={(v) => setForm(p => ({ ...p, project_type: v }))}>
                <SelectTrigger data-testid="feasibility-select-type" className="rounded-sm h-11"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5 flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> Building Type
              </Label>
              <Select value={form.building_type} onValueChange={(v) => setForm(p => ({ ...p, building_type: v }))}>
                <SelectTrigger data-testid="feasibility-select-building-type" className="rounded-sm h-11"><SelectValue placeholder="Select building type" /></SelectTrigger>
                <SelectContent>{BUILDING_TYPES.flatMap(g => g.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5 flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> Location *
              </Label>
              <Select value={form.location} onValueChange={(v) => setForm(p => ({ ...p, location: v }))}>
                <SelectTrigger data-testid="feasibility-select-location" className="rounded-sm h-11"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5 flex items-center gap-1.5">
                <Ruler className="w-3 h-3" /> Stand Size (m²) *
              </Label>
              <Input data-testid="feasibility-input-stand" type="number" value={form.stand_size} onChange={(e) => setForm(p => ({ ...p, stand_size: e.target.value }))} placeholder="e.g. 800" className="rounded-sm h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5 flex items-center gap-1.5">
                <Wallet className="w-3 h-3" /> Budget (R) *
              </Label>
              <Input data-testid="feasibility-input-budget" type="number" value={form.budget} onChange={(e) => setForm(p => ({ ...p, budget: e.target.value }))} placeholder="e.g. 5000000" className="rounded-sm h-11" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Finish Level</Label>
              <Select value={form.finish_level} onValueChange={(v) => setForm(p => ({ ...p, finish_level: v }))}>
                <SelectTrigger data-testid="feasibility-select-finish" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{FINISH_LEVELS.map(f => <SelectItem key={f.value} value={f.value}>{f.label} ({f.desc})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <Button data-testid="feasibility-calculate-btn" onClick={handleCalculate} disabled={loading} className="mt-8 bg-brand-dark text-white hover:bg-gray-800 rounded-sm h-12 px-10 font-body text-sm font-semibold uppercase tracking-wider">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing...</> : <>Understand Your Options <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="feasibility-results">
              {/* Feasibility Indicator */}
              <div className={`rounded-sm border p-6 ${fStyle.bg} ${fStyle.border}`}>
                <div className="flex items-center gap-3 mb-3">
                  <FIcon className={`w-6 h-6 ${fStyle.text}`} />
                  <h3 className={`font-heading text-2xl font-medium capitalize ${fStyle.text}`} data-testid="feasibility-indicator">{result.feasibility}</h3>
                </div>
                <p className="text-brand-dark/60 font-body text-sm">
                  Your project is rated as <strong className="capitalize">{result.feasibility}</strong> based on your budget of {formatZARFull(result.budget)} against an estimated all-in cost range.
                </p>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Buildable Area', value: `${result.buildable_area}m²`, testId: 'metric-buildable' },
                  { label: 'Cost Range', value: `${formatZARFull(result.cost_range.min)} - ${formatZARFull(result.cost_range.max)}`, testId: 'metric-cost' },
                  { label: 'Affordable Area', value: `${result.affordable_sqm.min} - ${result.affordable_sqm.max}m²`, testId: 'metric-affordable' },
                  { label: 'Complexity', value: `${result.complexity}/10`, testId: 'metric-complexity' },
                ].map((m) => (
                  <div key={m.testId} className="bg-white rounded-sm border border-black/10 p-5" data-testid={m.testId}>
                    <p className="text-xs uppercase tracking-wider font-semibold text-brand-dark/40 mb-1 font-heading">{m.label}</p>
                    <p className="font-heading text-lg font-medium text-brand-dark">{m.value}</p>
                  </div>
                ))}
              </div>

              {/* Cost-optimisation reassurance (mirrored from PDF) */}
              <div className="flex items-start gap-3 px-4 py-3 border-l-2 border-brand-accent/60 bg-brand-light/40 rounded-sm" data-testid="cost-optimisation-note">
                <p className="text-brand-dark/70 font-body text-sm italic leading-relaxed">
                  These figures describe a starting position. Architecture is the process that refines scope and improves outcomes.
                </p>
              </div>

              {/* Architect-Guided Insight */}
              <div className="bg-brand-light rounded-sm border border-brand-accent/15 p-6" data-testid="ai-explanation-section">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-brand-accent" />
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60">What This Means for Your Project</h3>
                </div>
                <p className="text-brand-dark/30 text-xs font-body mb-3">Architect-guided insight</p>
                {aiLoading ? (
                  <div className="flex items-center gap-2 text-brand-dark/40 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Interpreting your results...</div>
                ) : aiExplanation ? (
                  <div className="prose prose-sm max-w-none text-brand-dark/70 font-body leading-relaxed whitespace-pre-wrap" data-testid="ai-explanation-text">{aiExplanation}</div>
                ) : (
                  <p className="text-brand-dark/40 text-sm font-body">Architect-guided insights will appear after your results are generated.</p>
                )}
              </div>

              <Separator />

              {/* PDF Download + Ask + Contact */}
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                <Button data-testid="feasibility-pdf-btn" onClick={handleDownloadPDF} variant="outline" className="border-brand-dark text-brand-dark hover:bg-brand-dark/5 rounded-sm h-11 px-8 font-body font-semibold text-sm uppercase tracking-wider">
                  <Download className="w-4 h-4 mr-2" /> Download PDF Report
                </Button>
                <Button data-testid="feasibility-ask-btn" variant="outline" onClick={() => navigate('/ask-architect', { state: { context: { type: 'feasibility', data: { location: result.location, stand_size: result.stand_size, budget: result.budget, feasibility: result.feasibility, complexity: result.complexity, cost_min: result.cost_range?.min, cost_max: result.cost_range?.max } } } })} className="border-brand-dark/30 text-brand-dark/70 hover:bg-brand-dark/5 rounded-sm h-11 px-8 font-body font-semibold text-sm uppercase tracking-wider">
                  <MessageSquare className="w-4 h-4 mr-2" /> What to Think About Next
                </Button>
                {!showContact && (
                  <Button data-testid="feasibility-save-btn" onClick={() => setShowContact(true)} className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm h-11 px-8 font-body font-semibold text-sm uppercase tracking-wider">
                    Discuss with an Architect <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>

              {/* "We create space" transition */}
              <p className="text-center text-brand-accent-dark/60 font-heading italic text-sm mt-2">
                We create space — by removing uncertainty.
              </p>

              {showContact && (
                <div className="bg-white rounded-sm border border-black/10 p-6">
                  <h3 className="font-heading text-lg font-medium mb-4">Your Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Name</Label>
                      <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Your name" className="rounded-sm h-10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Email *</Label>
                      <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@email.com" className="rounded-sm h-10 mt-1" required />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Phone</Label>
                      <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+27 ..." className="rounded-sm h-10 mt-1" />
                    </div>
                  </div>
                  <Button data-testid="contact-submit-btn" onClick={handleSaveContact} className="mt-4 bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-sm font-semibold uppercase tracking-wider">
                    Save & Send My Report
                  </Button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ReportLeadGate
        open={showGate}
        onClose={() => setShowGate(false)}
        onSubmit={handleGateSubmit}
        toolLabel="Feasibility Report"
        initial={{ name: form.name, email: form.email, phone: form.phone }}
      />
    </div>
  );
}
